import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../../firebase/config";
import { sendEmailVerification } from "firebase/auth";
import PrimaryButton from "../../../components/Buttons/PrimaryButton";
import Config from "../../../Config";

import { Mail } from "lucide-react";

const VerifyEmail: React.FC = () => {
  const [email, setEmail] = useState<string>("");
  const [resending, setResending] = useState<boolean>(false);
  const [resendMessage, setResendMessage] = useState<string>("");
  const navigate = useNavigate();

  useEffect(() => {
    const pendingEmail = sessionStorage.getItem("pendingVerificationEmail");

    if (!pendingEmail) {
      navigate("/signup");
      return;
    }

    setEmail(pendingEmail);

    // check if user is already verified (refreshes every 3 sec)
    const checkVerification = setInterval(async () => {
      const user = auth.currentUser;
      if (user) {
        await user.reload();
        if (user.emailVerified) {
          clearInterval(checkVerification);

          const pendingOrgSetupToken = sessionStorage.getItem("pendingOrgSetupToken");
          const pendingOrgAcceptToken = sessionStorage.getItem("pendingOrgAcceptToken");

          if (pendingOrgSetupToken || pendingOrgAcceptToken) {
            try {
              const firebaseToken = await user.getIdToken(true);

              const loginResponse = await fetch(
                `${Config.API_URL}/api/auth/firebase/login`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${firebaseToken}`,
                  },
                }
              );

              if (!loginResponse.ok) {
                throw new Error("Failed to authenticate with backend");
              }

              const loginData = await loginResponse.json();
              const backendToken = loginData.access_token;

              if (pendingOrgSetupToken) {
                const orgSetupResponse = await fetch(
                  `${Config.API_URL}/api/organization/setup`,
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${backendToken}`,
                      "X-Firebase-Authorization": `Bearer ${firebaseToken}`,
                    },
                    body: JSON.stringify({ token: pendingOrgSetupToken }),
                  }
                );

                if (!orgSetupResponse.ok) {
                  const errorData = await orgSetupResponse.json().catch(() => ({}));
                  console.error("Organization setup failed:", errorData.detail);
                }

                sessionStorage.removeItem("pendingOrgSetupToken");
              }

              if (pendingOrgAcceptToken) {
                const orgAcceptResponse = await fetch(
                  `${Config.API_URL}/api/organization/accept-invitation`,
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${backendToken}`,
                      "X-Firebase-Authorization": `Bearer ${firebaseToken}`,
                    },
                    body: JSON.stringify({ token: pendingOrgAcceptToken }),
                  }
                );

                if (!orgAcceptResponse.ok) {
                  const errorData = await orgAcceptResponse.json().catch(() => ({}));
                  console.error("Organization accept failed:", errorData.detail);
                }

                sessionStorage.removeItem("pendingOrgAcceptToken");
              }
            } catch (error) {
              console.error("Failed to complete organization setup/accept:", error);
            }
          }

          sessionStorage.removeItem("pendingVerificationEmail");
          sessionStorage.removeItem("pendingVerificationName");
          sessionStorage.setItem("emailVerified", "true");
          navigate("/login");
        }
      }
    }, 3000);

    return () => clearInterval(checkVerification);
  }, [navigate]);

  const handleResendEmail = async () => {
    setResending(true);
    setResendMessage("");

    try {
      const user = auth.currentUser;
      if (user) {
        await sendEmailVerification(user, {
          url: `${window.location.origin}/login`,
        });
        setResendMessage("Verification email sent! Please check your inbox.");
      } else {
        setResendMessage("Error: No user found. Please sign up again.");
      }
    } catch (error: any) {
      console.error("Resend error:", error);
      setResendMessage(
        error.message || "Failed to resend email. Please try again."
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex justify-center items-center bg-gray-50">
      <div className="w-full max-w-md px-4">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <div className="mb-6">
            <Mail className="mx-auto text-[#1d365d]" size={50} />
          </div>

          <h2 className="text-3xl font-semibold mb-4">Verify your email</h2>

          <p className="text-gray-600 mb-6">
            We've sent a verification email to:
          </p>

          <p className="text-lg font-medium text-[#1d365d] mb-6">{email}</p>

          <p className="text-gray-600 mb-8">
            Please click the link in the email to verify your account and
            complete registration.
          </p>

          {resendMessage && (
            <div
              className={`mb-4 px-4 py-3 rounded ${
                resendMessage.includes("Error") ||
                resendMessage.includes("Failed")
                  ? "bg-red-100 border border-red-400 text-red-700"
                  : "bg-green-100 border border-green-400 text-green-700"
              }`}
              role="alert"
            >
              {resendMessage}
            </div>
          )}

          <PrimaryButton
            onClick={handleResendEmail}
            disabled={resending}
            className="w-full"
          >
            {resending ? "Sending..." : "Resend verification email"}
          </PrimaryButton>

          <p className="mt-6 text-sm text-gray-500">
            After verifying, you'll be automatically redirected to the
            dashboard.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
